import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { categories } from "@/mocks/categories";

export default function Header() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const mobileRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
      if (mobileRef.current && !mobileRef.current.contains(e.target as Node)) {
        setMobileMenuOpen(false);
        setDropdownOpen(false);
      }
    }
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-background-50 border-b-2 border-background-300">
      <div className="w-full px-4 md:px-10">
        <div className="flex items-center justify-between h-16 md:h-[72px]">
          <Link to="/" className="flex items-center gap-2 shrink-0 cursor-pointer">
            <div className="w-9 h-9 rounded-lg bg-primary-500 flex items-center justify-center">
              <i className="ri-store-2-line text-background-50 text-lg"></i>
            </div>
            <span className="font-heading text-lg md:text-xl font-bold text-foreground-900 whitespace-nowrap">
              Sugargoo Spreadsheet
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-2" ref={dropdownRef}>
            <Link
              to="/"
              className="flex items-center gap-1.5 px-4 py-2.5 rounded-full text-foreground-700 font-medium text-sm hover:bg-secondary-100 hover:text-foreground-900 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-home-4-line text-base"></i>
              Home
            </Link>

            <Link
              to="/guide/how-to-use-sugargoo-spreadsheet"
              className="flex items-center gap-1.5 px-4 py-2.5 rounded-full text-foreground-700 font-medium text-sm hover:bg-secondary-100 hover:text-foreground-900 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-book-open-line text-base"></i>
              Shopping Guide
            </Link>

            <div className="relative">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setDropdownOpen(!dropdownOpen);
                }}
                className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-secondary-100 text-foreground-800 font-semibold text-sm hover:bg-secondary-200 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-grid-line text-base"></i>
                All Categories
                <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${dropdownOpen ? "rotate-180" : ""}`}></i>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 bg-background-50 rounded-xl border border-background-300 p-5 w-[680px] shadow-lg z-50 max-h-[70vh] overflow-y-auto">
                  <div className="grid grid-cols-4 gap-1.5">
                    {categories.map((cat) => (
                      <a
                        key={cat.name}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="px-3 py-2.5 rounded-lg text-sm text-foreground-700 hover:bg-secondary-100 hover:text-foreground-900 transition-colors whitespace-nowrap cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDropdownOpen(false);
                        }}
                      >
                        {cat.name}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 dark:text-foreground-950 font-semibold text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Sugargoo Spreadsheet
              <i className="ri-arrow-right-up-line text-sm"></i>
            </a>
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center gap-1.5 px-5 py-2.5 rounded-full border-2 border-primary-500 text-primary-600 font-semibold text-sm hover:bg-primary-500 hover:text-background-50 transition-colors whitespace-nowrap cursor-pointer"
            >
              Buy It Now
              <i className="ri-shopping-bag-line text-sm"></i>
            </a>
          </nav>

          <div ref={mobileRef} className="md:hidden relative">
            <div className="flex items-center gap-2">
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="px-3 py-2 rounded-full bg-primary-500 text-background-50 font-semibold text-xs whitespace-nowrap cursor-pointer"
              >
                Buy Now
              </a>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setMobileMenuOpen(!mobileMenuOpen);
                  setDropdownOpen(false);
                }}
                className="w-9 h-9 rounded-lg bg-secondary-100 flex items-center justify-center cursor-pointer"
              >
                <i className={`${mobileMenuOpen ? "ri-close-line" : "ri-menu-line"} text-foreground-800 text-lg`}></i>
              </button>
            </div>

            {mobileMenuOpen && (
              <div className="absolute top-full right-0 mt-2 bg-background-50 rounded-xl border border-background-300 shadow-lg z-50 w-[300px] max-w-[calc(100vw-2rem)] pb-4 pt-4 max-h-[70vh] overflow-y-auto">
                <div className="flex flex-col gap-1 mb-3 px-3">
                  <Link
                    to="/"
                    onClick={(e) => {
                      e.stopPropagation();
                      setMobileMenuOpen(false);
                    }}
                    className="flex items-center gap-2 px-4 py-3 rounded-lg text-sm font-semibold text-foreground-800 hover:bg-secondary-100 transition-colors cursor-pointer"
                  >
                    <i className="ri-home-4-line text-base"></i>
                    Home
                  </Link>
                  <Link
                    to="/guide/how-to-use-sugargoo-spreadsheet"
                    onClick={(e) => {
                      e.stopPropagation();
                      setMobileMenuOpen(false);
                    }}
                    className="flex items-center gap-2 px-4 py-3 rounded-lg text-sm font-semibold text-foreground-800 hover:bg-secondary-100 transition-colors cursor-pointer"
                  >
                    <i className="ri-book-open-line text-base"></i>
                    Shopping Guide
                  </Link>
                </div>
                <p className="px-6 py-2 text-xs font-semibold uppercase tracking-widest text-foreground-500">
                  All Categories
                </p>
                <div className="grid grid-cols-1 gap-1 px-3">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm text-foreground-700 hover:bg-secondary-100 transition-colors cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        setMobileMenuOpen(false);
                      }}
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-primary-400 shrink-0"></span>
                      {cat.name}
                    </a>
                  ))}
                </div>
                <div className="mt-3 flex flex-col gap-2 px-5">
                  <a
                    href="https://kaspreadsheet.com/"
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-primary-500 text-background-50 font-semibold text-sm cursor-pointer"
                  >
                    Sugargoo Spreadsheet
                    <i className="ri-arrow-right-up-line text-sm"></i>
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}